// Lead Generation System — the graphical weekly grade badge for the console.
//
// Reads the AUTHORITATIVE grade endpoint (leadGenGradeApi) — the same engine
// the internal earnings page uses — never an FE-computed guess. A large,
// color-coded letter sits inside a circular progress ring (= gradePercent, the
// share of the weekly cap earned), beside the projected payout and a
// "distance to next grade" nudge.
//
// Projected vs final: while the badge's week is still OPEN it reads
// "projected"; once that week has closed (its Monday is before the current
// Monday-UTC) it flips to "final". Display/report only — no number is changed
// or recomputed here.
//
// Colors map the requested band ramp (A green · B teal · C amber · D coral ·
// F red) onto the design tokens that exist — success / info / gold / warning /
// error — using each token as the saturated "dark stop" for the letter + ring
// and a 10% tint of the same token as the light background. No hardcoded hex.

import { Card } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { formatCurrency } from "@/design-system/lib/format";
import type { LeadGenGradeResult } from "../../api/leadGenGrade";

/** Cents everywhere on this surface (Part A #1) — 2-dp, never whole dollars. */
function money(amount: number | null | undefined): string {
  return formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Band → design-token styling ──────────────────────────────────────────────

interface BandStyle {
  /** Saturated text/stroke class (the ramp's dark stop). */
  accent: string;
  /** 10–15% tint of the same token for the badge's soft background. */
  tint: string;
  encouragement: string;
}

const BAND_FALLBACK: BandStyle = {
  accent: "text-onyx",
  tint: "bg-white-warm",
  encouragement: "Keep the line moving.",
};

const BAND_STYLE: Record<string, BandStyle> = {
  // A — green
  A: {
    accent: "text-status-success",
    tint: "bg-status-success/10",
    encouragement: "Top of the board — that's your best bonus. Keep the line moving.",
  },
  // B — teal (nearest cool token: info)
  B: {
    accent: "text-status-info",
    tint: "bg-status-info/10",
    encouragement: "Strong week. A little more push tips you into A territory.",
  },
  // C — amber (gold ramp: gold-shadow is the readable dark stop)
  C: {
    accent: "text-gold-shadow",
    tint: "bg-gold/15",
    encouragement: "Solid pace. Tighten your targeting to climb a grade.",
  },
  // D — coral (nearest warm token: warning)
  D: {
    accent: "text-status-warning",
    tint: "bg-status-warning/10",
    encouragement: "Below target this week — focus on better-fit listings.",
  },
  // F — red
  F: {
    accent: "text-status-error",
    tint: "bg-status-error/10",
    encouragement: "Slow start — work the line to get your credits up.",
  },
};

function styleFor(band: string): BandStyle {
  return BAND_STYLE[band] ?? BAND_FALLBACK;
}

// ── Projected vs final ───────────────────────────────────────────────────────

/** The Monday (UTC midnight) of the week containing `d` — mirrors the backend
 *  pay-week anchor. */
function mondayOfUtc(d: Date): number {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = (date.getUTCDay() + 6) % 7; // 0 = Monday
  date.setUTCDate(date.getUTCDate() - dow);
  return date.getTime();
}

/** A grade whose pay week has already closed is FINAL; the open week is a
 *  projection. */
export function isGradeWeekFinal(weekOfIso: string): boolean {
  const wk = new Date(weekOfIso);
  if (Number.isNaN(wk.getTime())) return false;
  return mondayOfUtc(wk) < mondayOfUtc(new Date());
}

// ── Circular progress ring ───────────────────────────────────────────────────

function GradeRing({
  fractionPercent,
  letter,
  accent,
}: {
  /** 0..100 (clamped) — the share of the weekly cap earned. */
  fractionPercent: number;
  letter: string;
  accent: string;
}) {
  const r = 42;
  const circumference = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, fractionPercent));
  const offset = circumference * (1 - pct / 100);
  return (
    <div className="relative inline-flex shrink-0 items-center justify-center">
      <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90" aria-hidden="true">
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          strokeWidth="8"
          className="text-border-light"
          stroke="currentColor"
        />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          className={cn("transition-[stroke-dashoffset] duration-slow", accent)}
          stroke="currentColor"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className={cn("absolute font-display text-h2 font-bold leading-none", accent)}>
        {letter}
      </span>
    </div>
  );
}

// ── The badge ────────────────────────────────────────────────────────────────

export function LeadGenGradeBadge({ grade }: { grade: LeadGenGradeResult }) {
  const style = styleFor(grade.band);
  const final = isGradeWeekFinal(grade.weekOf);
  // Multiplier uplift: Net = Round(gross × mult) + adjustments, so the bonus
  // (multiplier-only) portion is Net − adjustments − gross.
  const bonus = grade.approvedNet - grade.totalAdjustments - grade.approvedGross;
  const hasAdjustments = Math.abs(grade.totalAdjustments) >= 0.005;
  const pulseWhenA =
    grade.band === "A" && !final ? "animate-in fade-in zoom-in-95 duration-slow" : undefined;

  return (
    <Card
      className={cn("space-y-space-4", style.tint, pulseWhenA)}
      data-testid="leadgen-grade-badge"
    >
      <div className="flex flex-col items-center gap-space-4 sm:flex-row sm:items-center">
        <div className="flex flex-col items-center gap-space-1" title={style.encouragement}>
          <GradeRing
            fractionPercent={grade.gradePercent}
            letter={grade.letter}
            accent={style.accent}
          />
          <p className="text-caption text-gray-mid m-0">
            {Math.round(Math.min(100, Math.max(0, grade.gradePercent)))}% of weekly cap
          </p>
        </div>

        <div className="min-w-0 flex-1 space-y-space-2 text-center sm:text-left">
          <p className="text-caption uppercase tracking-wide text-gray-mid m-0">
            {final ? "Final grade" : "Projected grade"} · {money(grade.weeklyCap)} weekly cap
          </p>
          <p
            className="text-h2 font-display font-bold text-onyx m-0"
            data-testid="leadgen-grade-payout"
          >
            {money(grade.approvedNet)}
          </p>
          <p className="text-small text-gray-mid m-0">
            <span className="font-semibold text-onyx">{money(grade.approvedGross)}</span> base
            {bonus > 0.005 ? (
              <>
                {" "}+ <span className={cn("font-semibold", style.accent)}>{money(bonus)}</span>{" "}
                bonus ({grade.multiplier.toFixed(2)}×)
              </>
            ) : (
              <> · {grade.multiplier.toFixed(2)}× bonus</>
            )}
            {hasAdjustments && (
              <>
                {" "}·{" "}
                <span className="font-semibold text-onyx">
                  {grade.totalAdjustments >= 0 ? "+" : "−"}
                  {money(Math.abs(grade.totalAdjustments))}
                </span>{" "}
                adjustments
              </>
            )}
          </p>

          {grade.nextLetter && (
            <p className="text-small text-onyx m-0" data-testid="leadgen-grade-next">
              <span className="font-semibold">{money(grade.distanceToNext)}</span> more this week
              for {gradeArticle(grade.nextLetter)}{" "}
              <span className="font-semibold">{grade.nextLetter}</span> and a{" "}
              <span className="font-semibold">{grade.nextMultiplier.toFixed(2)}×</span> bonus.
            </p>
          )}
        </div>
      </div>

      <p className="text-caption text-gray-mid m-0">
        {final
          ? "This week has closed — your manager finalizes the payable amount, and pay is handled off-system."
          : "Projected from your approved credits so far. Your manager finalizes what's payable; pay is handled off-system."}
      </p>
    </Card>
  );
}

/** "an" before A/F (vowel-sounding letters), "a" otherwise. */
function gradeArticle(letter: string): string {
  const head = letter.trim().charAt(0).toUpperCase();
  return head === "A" || head === "F" ? "an" : "a";
}

// ── Compact chip (Home) ──────────────────────────────────────────────────────

/** A small band-colored grade pill for the Home hero — the authoritative grade
 *  in a glance. */
export function LeadGenGradeChip({
  grade,
  className,
}: {
  grade: LeadGenGradeResult;
  className?: string;
}) {
  const style = styleFor(grade.band);
  const final = isGradeWeekFinal(grade.weekOf);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-space-2 rounded-pill px-space-3 py-space-1",
        style.tint,
        className,
      )}
      title={style.encouragement}
      aria-label={`${final ? "Final" : "Projected"} grade ${grade.letter}, ${grade.multiplier.toFixed(
        2,
      )}× bonus`}
      data-testid="leadgen-grade-chip"
    >
      <span className={cn("font-display text-lead font-bold leading-none", style.accent)}>
        {grade.letter}
      </span>
      <span className="text-caption text-gray-mid">
        {final ? "Final" : "Projected"} · {grade.multiplier.toFixed(2)}× bonus
      </span>
    </span>
  );
}
